import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Loader2, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export const AuthModal = ({ open, onClose }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot' | 'forgot-sent'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'register') {
        if (!name.trim()) { setError('Please enter your name'); setSubmitting(false); return; }
        await register(name.trim(), email.trim(), password);
      } else {
        await login(email.trim(), password);
      }
      onClose();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setMode('forgot-sent');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || 'Failed to send reset email');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (!open) return null;

  const titles = {
    login: { heading: 'Welcome Back', sub: 'Sign in to view your orders & scans' },
    register: { heading: 'Create Account', sub: 'Join to track your orders & Ted AI scans' },
    forgot: { heading: 'Forgot Password', sub: 'Enter your email to receive a reset link' },
    'forgot-sent': { heading: 'Check Your Email', sub: 'We\'ve sent a reset link if an account exists' },
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="auth-modal"
        >
          {/* Header */}
          <div className="bg-[#0FA49C] p-5 text-white flex items-start justify-between">
            <div>
              <h3 className="font-bold text-lg">{titles[mode]?.heading}</h3>
              <p className="text-xs text-white/80 mt-0.5">{titles[mode]?.sub}</p>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg" data-testid="close-auth-modal">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {/* Forgot Password - Sent confirmation */}
            {mode === 'forgot-sent' && (
              <div className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-[#0FA49C]" />
                <p className="text-sm text-[#444] mb-1">If an account exists for <strong>{email}</strong>, we've sent a password reset link.</p>
                <p className="text-xs text-[#999] mb-4">Check your inbox (and spam folder). The link expires in 1 hour.</p>
                <Button
                  onClick={() => { setMode('login'); setError(''); }}
                  className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl px-6 py-4 font-bold text-sm"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Sign In
                </Button>
              </div>
            )}

            {/* Forgot Password - Email form */}
            {mode === 'forgot' && (
              <>
                <form onSubmit={handleForgotSubmit} className="space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      required
                      className="pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl py-5 font-bold"
                    data-testid="forgot-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Link'}
                  </Button>
                </form>

                <p className="text-center text-sm text-[#666]">
                  <button onClick={() => { setMode('login'); setError(''); }} className="text-[#0FA49C] font-semibold hover:underline" data-testid="back-to-login-link">
                    <ArrowLeft className="w-3 h-3 inline mr-1" />Back to Sign In
                  </button>
                </p>
              </>
            )}

            {/* Login / Register */}
            {(mode === 'login' || mode === 'register') && (
              <>
                {/* Google Login */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleLogin}
                  className="w-full py-5 font-semibold text-sm border-gray-300 hover:bg-gray-50"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-[#999] font-medium">or</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* Email Form */}
                <form onSubmit={handleSubmit} className="space-y-3">
                  {mode === 'register' && (
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="pl-10"
                        data-testid="auth-name-input"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      required
                      className="pl-10"
                      data-testid="auth-email-input"
                    />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      required
                      minLength={6}
                      className="pl-10"
                      data-testid="auth-password-input"
                    />
                  </div>

                  {mode === 'login' && (
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => { setMode('forgot'); setError(''); }}
                        className="text-xs text-[#0FA49C] font-semibold hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="auth-error">
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#0FA49C] hover:bg-[#0d8f88] text-white rounded-xl py-5 font-bold"
                    data-testid="auth-submit-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        {mode === 'login' ? 'Sign In' : 'Create Account'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                {/* Toggle mode */}
                <p className="text-center text-sm text-[#666]">
                  {mode === 'login' ? (
                    <>Don't have an account?{' '}
                      <button onClick={() => { setMode('register'); setError(''); }} className="text-[#0FA49C] font-semibold hover:underline" data-testid="switch-to-register">
                        Sign up
                      </button>
                    </>
                  ) : (
                    <>Already have an account?{' '}
                      <button onClick={() => { setMode('login'); setError(''); }} className="text-[#0FA49C] font-semibold hover:underline" data-testid="switch-to-login">
                        Sign in
                      </button>
                    </>
                  )}
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
